"use client"

import { useEffect, useState } from "react"
import { Type, ChevronDown, Check } from "lucide-react"
import { useLocale } from "next-intl"

type FontFamily = "geist" | "poppins" | "cairo"

export function FontSwitcher() {
  const locale = useLocale()
  const [mounted, setMounted] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [font, setFont] = useState<FontFamily>("geist")

  useEffect(() => {
    setMounted(true)
    // For Arabic, always use Cairo font
    if (locale === "ar") {
      setFont("cairo")
      applyFont("cairo")
      return
    }
    // Load saved font preference from localStorage for other languages
    const savedFont = localStorage.getItem("app-font") as FontFamily | null
    if (savedFont) {
      setFont(savedFont)
      applyFont(savedFont)
    }
  }, [locale])

  const fonts: { id: FontFamily; label: string; description: string; preview: string }[] = [
    { id: "geist", label: "Geist", description: "Modern & clean", preview: "Aa" },
    { id: "poppins", label: "Poppins", description: "Friendly & rounded", preview: "Aa" },
    { id: "cairo", label: "Cairo", description: "Arabic support", preview: "عربي" },
  ]

  const applyFont = (fontFamily: FontFamily) => {
    const root = document.documentElement
    if (fontFamily === "poppins") {
      root.style.setProperty("--font-applied", "var(--font-poppins)")
      document.body.style.fontFamily = "Poppins"
    } else if (fontFamily === "cairo") {
      root.style.setProperty("--font-applied", "var(--font-cairo)")
      document.body.style.fontFamily = "Cairo"
    } else {
      root.style.setProperty("--font-applied", "var(--font-geist)")
      document.body.style.fontFamily = "Geist"
    }
  }

  const handleFontChange = (newFont: FontFamily) => {
    setFont(newFont)
    applyFont(newFont)
    localStorage.setItem("app-font", newFont)
    setIsOpen(false)
  }

  if (!mounted || locale === "ar") return null

  const currentFont = fonts.find((f) => f.id === font)

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 sm:gap-2 text-white hover:text-white/90 transition-all bg-white/15 hover:bg-white/25 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-white/20 hover:border-white/30 min-w-[44px] min-h-[44px] justify-center"
        aria-label="Font selector"
      >
        <Type className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
        <span className="hidden md:inline text-xs font-medium truncate max-w-16">
          {currentFont?.label}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 z-50 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="absolute -top-1 right-4 w-3 h-3 bg-white dark:bg-gray-900 rotate-45 border-l border-t border-gray-200 dark:border-gray-700"></div>
            
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                Choose Font
              </div>
              
              <div className="py-1">
                {fonts.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => handleFontChange(f.id)}
                    className={`w-full px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between rounded-lg mx-1 ${
                      font === f.id ? "bg-primary/10 text-primary dark:bg-primary/20" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center text-sm font-semibold">
                        {f.preview}
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-sm">{f.label}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{f.description}</span>
                      </div>
                    </div>
                    {font === f.id && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
